import Box from "@mui/material/Box";

import { fromZonedDateTimeInput, toZonedDateTimeInput } from "@/libs/zonedDateTime";
import type { RegistrationHint } from "@/libs/tournamentSetupApi";

import { formatDay, formatTime, PickerField } from "./fields";
import { DenseButton, DenseSelect, FieldLabel, Note, tone, WarningIcon } from "./ui";

export type RegistrationWindow = {
  opens: "on_open_click" | "on_date";
  openAt: string | null;
  closeAt: string | null;
};

function split(iso: string | null, timeZone: string): { date: string; time: string } {
  const local = toZonedDateTimeInput(iso, timeZone);
  return local ? { date: local.slice(0, 10), time: `${local.slice(11, 16)}:00` } : { date: "", time: "23:59:00" };
}

function join(date: string, time: string, timeZone: string): string | null {
  return date ? fromZonedDateTimeInput(`${date}T${time.slice(0, 5)}`, timeZone) : null;
}

/** Opens (on click or on a date), closes date + time, and the short-window hint (F1). */
export function RegistrationWindowFields({
  value,
  onChange,
  hint,
  timeZone,
  tzLabel,
  phone,
}: {
  value: RegistrationWindow;
  onChange: (next: RegistrationWindow) => void;
  hint?: RegistrationHint | null;
  timeZone: string;
  tzLabel: string;
  phone?: boolean;
}) {
  const close = split(value.closeAt, timeZone);
  const open = split(value.openAt, timeZone);
  const suggest = hint?.suggested_close_at ?? null;
  const openSelect = (
    <DenseSelect
      id="reg-opens"
      onChange={(event) =>
        onChange({
          ...value,
          opens: event.target.value as RegistrationWindow["opens"],
          openAt: event.target.value === "on_date" ? value.openAt ?? new Date().toISOString() : null,
        })
      }
      sx={{ fontSize: 12.5 }}
      value={value.opens}
    >
      <option value="on_open_click">When I click Open registration</option>
      <option value="on_date">On a date</option>
    </DenseSelect>
  );
  const closeDate = (
    <PickerField
      display={close.date ? formatDay(close.date, true) : "Pick a date"}
      id="reg-close"
      label="Registration closes"
      onChange={(date) => onChange({ ...value, closeAt: join(date, close.time, timeZone) })}
      type="date"
      value={close.date}
    />
  );
  const closeTime = (
    <PickerField
      display={`${formatTime(close.time)} ${tzLabel}`}
      id="reg-close-time"
      label="Registration closes at"
      onChange={(time) => onChange({ ...value, closeAt: join(close.date, time, timeZone) })}
      type="time"
      value={close.time}
    />
  );
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {phone ? (
        <>
          <Box>
            <FieldLabel htmlFor="reg-opens">Opens</FieldLabel>
            {openSelect}
          </Box>
          <Box>
            <FieldLabel htmlFor="reg-close">Closes</FieldLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 1 }}>
              {closeDate}
              {closeTime}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "1.35fr 1fr 96px", gap: 1 }}>
          <Box>
            <FieldLabel htmlFor="reg-opens">Registration opens</FieldLabel>
            {openSelect}
          </Box>
          <Box>
            <FieldLabel htmlFor="reg-close">Closes</FieldLabel>
            {closeDate}
          </Box>
          <Box>
            <FieldLabel htmlFor="reg-close-time">at</FieldLabel>
            {closeTime}
          </Box>
        </Box>
      )}
      {value.opens === "on_date" ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 1 }}>
          <PickerField
            display={open.date ? `Opens ${formatDay(open.date, true)}` : "Pick a date"}
            label="Registration opens on"
            onChange={(date) => onChange({ ...value, openAt: join(date, open.time, timeZone) })}
            type="date"
            value={open.date}
          />
          <PickerField
            display={`${formatTime(open.time)} ${tzLabel}`}
            label="Registration opens at"
            onChange={(time) => onChange({ ...value, openAt: join(open.date, time, timeZone) })}
            type="time"
            value={open.time}
          />
        </Box>
      ) : null}
      {hint?.warning ? (
        <Note icon={<WarningIcon />} role="status" tone="amber">
          <span>
            {hint.warning_lead ? (
              <>
                <b>{hint.warning_lead}</b>
                {hint.warning.slice(hint.warning_lead.length)}
              </>
            ) : (
              hint.warning
            )}
          </span>
          <Box sx={{ flexGrow: 1 }} />
          {suggest && hint.suggestion_label ? (
            <DenseButton onClick={() => onChange({ ...value, closeAt: suggest })} sx={{ color: tone.warnNoteText }} variant="link">
              {hint.suggestion_label}
            </DenseButton>
          ) : null}
        </Note>
      ) : null}
    </Box>
  );
}
